<template>
    <!-- The full string is in the flow at every step — the part not yet written
         is merely invisible. That is what makes the line wrap identically from
         the first frame to the last: nothing below the caption moves as it is
         typed, and the last word cannot hop to a new line and back on the frame
         the caret leaves. The spans must stay adjacent; whitespace between them
         would be rendered. -->
    <span class="pl-console" :aria-label="text"><span
        aria-hidden="true">{{ shown }}</span><span
        v-if="typing" class="pl-caret" aria-hidden="true"></span><span
        class="pl-console__rest" aria-hidden="true">{{ rest }}</span></span>
</template>

<script setup>
/*
 * A line of text that arrives the way a terminal writes it, one character at a
 * time, instead of fading or sliding in. Nothing else on the lobby moves, so
 * this is the only motion on the screen — and it is motion that says a machine
 * is answering, which is the register the whole player area is written in.
 *
 * The whole string is on the element as its accessible name from the first
 * frame: a screen reader announcing a name letter by letter is not a reading
 * of that name. The animated copy is hidden from the tree entirely.
 *
 * Lines are chained rather than run together — `active` holds a line back until
 * the one above it emits `done`, so a screen writes its name first and then
 * its description, in that order, the way a console would.
 */
import {computed, onBeforeUnmount, ref, watch} from 'vue';

const props = defineProps({
    text: {type: String, default: ''},
    /* Milliseconds per character. A name is written briskly; a paragraph under
       it is written slower, so the two read as one machine working down the
       screen rather than as two things racing. */
    speed: {type: Number, default: 24},
    /* However long the line, it never outstays this. */
    maxDuration: {type: Number, default: 1800},
    /* False holds the line back entirely — nothing shown, nothing typed. */
    active: {type: Boolean, default: true},
});

const emit = defineEmits(['done']);

const shown = ref('');
const typing = ref(false);

const rest = computed(() => props.text.slice(shown.value.length));
let frame = null;

const reduced = computed(() => typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);

function stop() {
    if (frame) cancelAnimationFrame(frame);
    frame = null;
    typing.value = false;
}

function finish(text) {
    stop();
    shown.value = text;
    emit('done');
}

/* Driven off elapsed time on the frame clock rather than one timer per
   character. A background tab clamps timers to about a second, which would
   otherwise leave a name being spelled out at one letter per second for
   somebody who switched away and came back; on the frame clock that tab simply
   stops animating and the line is already written when they return. */
function run() {
    stop();
    const text = props.text;
    if (!props.active) {
        shown.value = '';
        return;
    }
    /* Nobody is watching a background tab, and its frame clock is stopped —
       typing there would leave the caption blank until the tab came forward.
       It is simply already written. */
    if (!text || reduced.value || (typeof document !== 'undefined' && document.hidden)) {
        finish(text);
        return;
    }
    const duration = Math.min(props.maxDuration, text.length * props.speed);
    const started = performance.now();
    shown.value = '';
    typing.value = true;
    const tick = (now) => {
        const progress = Math.min(1, (now - started) / duration);
        const count = Math.ceil(progress * text.length);
        shown.value = text.slice(0, count);
        if (progress >= 1) finish(text);
        else frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
}

watch(() => [props.text, props.active], run, {immediate: true});

onBeforeUnmount(stop);
</script>

<style scoped>
</style>
